/** An hour: an installed PWA can stay open for days without a navigation. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
// A gated sync repeats every 5 s; once a minute still catches the deploy that follows provisioning.
const GATED_CHECK_INTERVAL_MS = 60 * 1000;

const checkRequests = new Set<() => void>();

/**
 * Takes rowboat's 409 body. `schema_behind` means the tenant moved to a newer schema, so a
 * deploy is landing and every sync is refused until this tab reloads into it.
 */
export function onSyncGate(body: unknown): void {
  if ((body as { reason?: unknown } | null)?.reason !== 'schema_behind') return;
  for (const check of checkRequests) check();
}

export interface UpdateWatcher {
  /** Tells the waiting worker to take over. The page reloads on controllerchange. */
  apply(): void;
  stop(): void;
}

/**
 * Registers the worker and calls `onReady` once a new one is installed behind the one
 * controlling this page. The worker vite-plugin-pwa generates in `prompt` mode waits
 * until it is sent SKIP_WAITING.
 */
export async function watchForUpdates(
  onReady: () => void,
  container: ServiceWorkerContainer,
  reload: () => void = () => location.reload(),
): Promise<UpdateWatcher> {
  const registration = await container.register('/sw.js', { updateViaCache: 'none' });

  let applied = false;
  let reloading = false;
  // A controller change this page did not ask for is another tab taking the update;
  // reloading on it could drop an edit or a capture in progress here.
  const takeOver = () => {
    if (!applied) {
      onReady();
      return;
    }
    if (reloading) return;
    reloading = true;
    reload();
  };
  container.addEventListener('controllerchange', takeOver);

  // A first install has no controller, and nothing to reload into.
  if (registration.waiting && container.controller) onReady();

  const tracked = new WeakSet<ServiceWorker>();
  const trackInstalling = () => {
    const installing = registration.installing;
    if (!installing || tracked.has(installing)) return;
    tracked.add(installing);
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed' && container.controller) onReady();
    });
  };
  registration.addEventListener('updatefound', trackInstalling);
  // The navigation's own update check can fire updatefound before register resolves.
  trackInstalling();

  let lastCheck = performance.now();
  const checkOnVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (performance.now() - lastCheck < CHECK_INTERVAL_MS) return;
    lastCheck = performance.now();
    // A failed probe means offline or a server that is down; the next visibility change retries.
    registration.update().catch(() => {});
  };
  document.addEventListener('visibilitychange', checkOnVisible);

  let lastGatedCheck = Number.NEGATIVE_INFINITY;
  const checkOnGate = () => {
    if (registration.waiting) return;
    if (performance.now() - lastGatedCheck < GATED_CHECK_INTERVAL_MS) return;
    lastGatedCheck = performance.now();
    registration.update().catch(() => {});
  };
  checkRequests.add(checkOnGate);

  return {
    apply: () => {
      applied = true;
      const waiting = registration.waiting;
      // After another tab's apply() nothing is waiting and the new worker already
      // controls this page, so the reload alone picks it up.
      if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
      else reload();
    },
    stop: () => {
      container.removeEventListener('controllerchange', takeOver);
      registration.removeEventListener('updatefound', trackInstalling);
      document.removeEventListener('visibilitychange', checkOnVisible);
      checkRequests.delete(checkOnGate);
    },
  };
}
